import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

// Matches actual database schema
export interface MediaSessionState {
  id: string
  space_id: string
  platform: string | null
  video_id: string | null
  video_url: string | null
  title: string | null
  thumbnail_url: string | null
  duration: number | null
  playback_position: number
  state: 'playing' | 'paused' | 'stopped' | null
  leader_id: string | null
  started_by: string | null
  started_at: string | null
  ended_at: string | null
  last_sync_at: string | null
  is_active: boolean
  leader?: {
    id: string
    username: string
    display_name: string | null
  }
}

export interface QueueItem {
  id: string
  space_id: string
  platform: string | null
  video_id: string | null
  video_url: string | null
  title: string | null
  thumbnail_url: string | null
  duration: number | null
  added_by: string | null
  added_at: string
  position: number
  played: boolean
  adder?: {
    id: string
    username: string
    display_name: string | null
  }
}

interface UseMediaSessionOptions {
  spaceId: string
  userId: string
  onSessionChange?: (session: MediaSessionState | null) => void
  onQueueChange?: (queue: QueueItem[]) => void
}

export function useMediaSession({
  spaceId,
  userId,
  onSessionChange,
  onQueueChange
}: UseMediaSessionOptions) {
  const [session, setSession] = useState<MediaSessionState | null>(null)
  const [queue, setQueue] = useState<QueueItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Keep track of the last sync time to avoid excessive syncing
  const lastSyncTimeRef = useRef<number>(0)
  const SYNC_THROTTLE_MS = 500 // Only sync every 500ms max

  // Load initial session and queue
  useEffect(() => {
    if (spaceId) {
      loadSession()
      loadQueue()
    }
  }, [spaceId])

  // Set up real-time subscriptions
  useEffect(() => {
    if (!spaceId) return

    // Subscribe to session changes
    const sessionChannel = supabase
      .channel(`media_session:${spaceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'media_sessions',
          filter: `space_id=eq.${spaceId}`
        },
        async (payload) => {
          if (payload.eventType === 'DELETE') {
            setSession(null)
            onSessionChange?.(null)
          } else {
            // Fetch the complete session with leader info
            const { data } = await supabase
              .from('media_sessions')
              .select('*')
              .eq('id', payload.new.id)
              .single()

            if (data) {
              setSession(data as MediaSessionState)
              onSessionChange?.(data as MediaSessionState)
            }
          }
        }
      )
      .subscribe()

    // Subscribe to queue changes
    const queueChannel = supabase
      .channel(`media_queue:${spaceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'media_queue',
          filter: `space_id=eq.${spaceId}`
        },
        () => {
          // Reload queue on any change
          loadQueue()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(sessionChannel)
      supabase.removeChannel(queueChannel)
    }
  }, [spaceId, onSessionChange, onQueueChange])

  // Load session from database
  const loadSession = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('media_sessions')
        .select('*')
        .eq('space_id', spaceId)
        .eq('is_active', true)
        .maybeSingle()

      if (fetchError) {
        console.error('Error loading media session:', fetchError)
        setError(fetchError.message)
        return
      }

      setSession(data as MediaSessionState | null)
      onSessionChange?.(data as MediaSessionState | null)
    } catch (err) {
      console.error('Error loading media session:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  // Load queue from database
  const loadQueue = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('media_queue')
        .select(`
          *,
          adder:profiles!media_queue_added_by_fkey(
            id,
            username,
            display_name
          )
        `)
        .eq('space_id', spaceId)
        .eq('played', false)
        .order('position', { ascending: true })

      if (fetchError) {
        console.error('Error loading queue:', fetchError)
        return
      }

      const queueData = (data || []) as QueueItem[]
      setQueue(queueData)
      onQueueChange?.(queueData)
    } catch (err) {
      console.error('Error loading queue:', err)
    }
  }

  // Create a new session
  const createSession = async (
    platform: string,
    videoId: string,
    videoUrl: string,
    title?: string,
    thumbnailUrl?: string,
    duration?: number
  ) => {
    try {
      const { data, error: createError } = await supabase
        .from('media_sessions')
        .insert({
          space_id: spaceId,
          platform,
          video_id: videoId,
          video_url: videoUrl,
          title: title || null,
          thumbnail_url: thumbnailUrl || null,
          duration: duration || null,
          playback_position: 0,
          state: 'paused',
          leader_id: userId,
          started_by: userId,
          started_at: new Date().toISOString(),
          is_active: true,
          last_sync_at: new Date().toISOString()
        })
        .select()
        .single()

      if (createError) {
        console.error('Error creating session:', createError)
        throw createError
      }

      return data
    } catch (err) {
      console.error('Error creating session:', err)
      throw err
    }
  }

  // Update playback state (throttled to prevent excessive updates)
  const updatePlayback = useCallback(async (updates: Partial<MediaSessionState>) => {
    if (!session) return

    const now = Date.now()
    if (now - lastSyncTimeRef.current < SYNC_THROTTLE_MS) {
      // Too soon, skip this update
      return
    }
    lastSyncTimeRef.current = now

    try {
      const { error: updateError } = await supabase
        .from('media_sessions')
        .update({
          ...updates,
          last_sync_at: new Date().toISOString()
        })
        .eq('id', session.id)

      if (updateError) {
        console.error('Error updating playback:', updateError)
        throw updateError
      }
    } catch (err) {
      console.error('Error updating playback:', err)
      throw err
    }
  }, [session])

  // Play media
  const play = useCallback(async (playbackPosition?: number) => {
    await updatePlayback({
      state: 'playing',
      playback_position: playbackPosition ?? session?.playback_position ?? 0
    })
  }, [updatePlayback, session])

  // Pause media
  const pause = useCallback(async (playbackPosition?: number) => {
    await updatePlayback({
      state: 'paused',
      playback_position: playbackPosition ?? session?.playback_position ?? 0
    })
  }, [updatePlayback, session])

  // Seek to a specific time
  const seek = useCallback(async (time: number) => {
    await updatePlayback({
      playback_position: time
    })
  }, [updatePlayback])

  // Take control of the session
  const takeControl = useCallback(async () => {
    if (!session) return

    try {
      const { error: updateError } = await supabase
        .from('media_sessions')
        .update({ leader_id: userId })
        .eq('id', session.id)

      if (updateError) {
        console.error('Error taking control:', updateError)
        throw updateError
      }
    } catch (err) {
      console.error('Error taking control:', err)
      throw err
    }
  }, [session, userId])

  // Add item to queue
  const addToQueue = useCallback(async (
    platform: string,
    videoId: string,
    videoUrl: string,
    title?: string,
    thumbnailUrl?: string,
    duration?: number
  ) => {
    try {
      // Get next position
      const { data: maxPosData } = await supabase
        .from('media_queue')
        .select('position')
        .eq('space_id', spaceId)
        .order('position', { ascending: false })
        .limit(1)
        .maybeSingle()

      const nextPosition = (maxPosData?.position || 0) + 1

      const { data, error: insertError } = await supabase
        .from('media_queue')
        .insert({
          space_id: spaceId,
          platform,
          video_id: videoId,
          video_url: videoUrl,
          title: title || null,
          thumbnail_url: thumbnailUrl || null,
          duration: duration || null,
          added_by: userId,
          position: nextPosition,
          played: false
        })
        .select()
        .single()

      if (insertError) {
        console.error('Error adding to queue:', insertError)
        throw insertError
      }

      return data
    } catch (err) {
      console.error('Error adding to queue:', err)
      throw err
    }
  }, [spaceId, userId])

  // Remove item from queue
  const removeFromQueue = useCallback(async (itemId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('media_queue')
        .delete()
        .eq('id', itemId)

      if (deleteError) {
        console.error('Error removing from queue:', deleteError)
        throw deleteError
      }
    } catch (err) {
      console.error('Error removing from queue:', err)
      throw err
    }
  }, [])

  // Play next item in queue
  const playNext = useCallback(async () => {
    if (queue.length === 0) return

    const nextItem = queue[0]

    try {
      // Update session with next item
      await updatePlayback({
        platform: nextItem.platform,
        video_id: nextItem.video_id,
        video_url: nextItem.video_url,
        title: nextItem.title,
        thumbnail_url: nextItem.thumbnail_url,
        duration: nextItem.duration,
        playback_position: 0,
        state: 'playing'
      })

      // Mark as played
      await supabase
        .from('media_queue')
        .update({ played: true })
        .eq('id', nextItem.id)

    } catch (err) {
      console.error('Error playing next:', err)
      throw err
    }
  }, [queue, updatePlayback])

  // Clear entire queue
  const clearQueue = useCallback(async () => {
    try {
      const { error: deleteError } = await supabase
        .from('media_queue')
        .delete()
        .eq('space_id', spaceId)

      if (deleteError) {
        console.error('Error clearing queue:', deleteError)
        throw deleteError
      }
    } catch (err) {
      console.error('Error clearing queue:', err)
      throw err
    }
  }, [spaceId])

  // End session
  const endSession = useCallback(async () => {
    if (!session) return

    try {
      const { error: updateError } = await supabase
        .from('media_sessions')
        .update({
          is_active: false,
          ended_at: new Date().toISOString(),
          state: 'stopped'
        })
        .eq('id', session.id)

      if (updateError) {
        console.error('Error ending session:', updateError)
        throw updateError
      }
    } catch (err) {
      console.error('Error ending session:', err)
      throw err
    }
  }, [session])

  return {
    // State
    session,
    queue,
    loading,
    error,

    // Session actions
    createSession,
    loadSession,
    takeControl,
    endSession,

    // Playback controls
    play,
    pause,
    seek,
    updatePlayback,

    // Queue actions
    addToQueue,
    removeFromQueue,
    playNext,
    clearQueue,
    loadQueue
  }
}
